import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

// Simple utility if not present
function classNames(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(' ');
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  fullWidth?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, fullWidth = true, onFocus, onBlur, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const isPassword = type === 'password';
    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

    return (
      <div className={classNames("flex flex-col gap-[6px] items-start relative shrink-0", fullWidth ? "w-full" : "w-[494px]", className)}>
        {/* Label */}
        <div className="flex flex-col font-medium justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] w-full">
          <p className="leading-[1.8]">{label}</p>
        </div>

        {/* Input Wrapper */}
        <div 
          className={classNames(
            "h-[52px] relative rounded-[12px] shrink-0 w-full group transition-all duration-200 border border-solid flex flex-row items-center overflow-hidden",
            error ? "border-red-500" : (isFocused ? "border-[#2C9944]" : "border-[#E4E7EC]"),
            isFocused && !error ? "bg-white shadow-[0_0_0_4px_rgba(44,153,68,0.05)]" : "bg-[#F9FAFB]"
          )}
        >
          <div className="content-stretch flex items-center justify-between px-[12px] relative size-full">
            <input
              ref={ref}
              type={inputType}
              className={classNames(
                "flex-1 bg-transparent border-none outline-none text-[16px] h-full w-full",
                "text-[#1d2739] placeholder:text-[#667185]",
                "file:border-0 file:bg-transparent file:text-sm file:font-medium",
                "disabled:cursor-not-allowed disabled:opacity-50"
              )}
              onFocus={(e) => {
                setIsFocused(true);
                onFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                onBlur?.(e);
              }}
              {...props}
            />
            
            {/* Password Toggle */}
            {isPassword && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="ml-2 text-[#475367] hover:text-[#1d2739] transition-colors focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff className="w-[20px] h-[20px]" strokeWidth={1.25} />
                ) : (
                  <Eye className="w-[20px] h-[20px]" strokeWidth={1.25} />
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <span className="text-red-500 text-sm font-sans mt-1 animate-in slide-in-from-top-1 fade-in duration-200">
            {error}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

